import type { Entry, EntrySkeletonType } from 'contentful'

import type { ComponentContentTypes } from '@constants/enums/content-types'

import type { TypeAssetImageEntry } from './asset-image'
import type { TypeAssetPdfEntry } from './asset-pdf'
import type { TypeAssetVideoEntry } from './asset-video'
import type { TypeComponentCtaButtonEntry } from './component-cta-button'
import type { TypeComponentCtaGroupEntry } from './component-cta-group'
import type { TypeComponentLinkEntry } from './component-link'
import type { TypeItemTestimonialEntry } from './item-testimonial'
import type { TypePageEntry } from './page'
import type { TypeSectionFaqEntry } from './section-faq'
import type { TypeSectionGroupEntry } from './section-group'
import type { TypeSectionHeroEntry } from './section-hero'
import type { TypeSectionMultiColumnEntry } from './section-multi-column'
import type { TypeSectionSingleColumnEntry } from './section-single-column'
import type { TypeSectionTabsEntry } from './section-tabs'
import type { TypeSectionTestimonialsEntry } from './section-testimonials'
import type { TypeSectionTwoColumnEntry } from './section-two-column'
import type { TypeSeoMetadataEntry } from './seo-metadata'
import type { TypeSingleColumnEntry } from './single-column'
import type { UnresolvedEntryLink } from './unresolved-link'
import type { TypeVariationContainerAuthAndDeviceTypeCtaEntry } from './variation-container-auth-and-device-type-cta'
import type { TypeVariationContainerDeviceTypeEntry } from './variation-container-device-type'

export type EntryTypeMap = {
  [ComponentContentTypes.Page]: TypePageEntry
  [ComponentContentTypes.SeoMetadata]: TypeSeoMetadataEntry
  [ComponentContentTypes.AssetImage]: TypeAssetImageEntry
  [ComponentContentTypes.AssetVideo]: TypeAssetVideoEntry
  [ComponentContentTypes.AssetPdf]: TypeAssetPdfEntry
  [ComponentContentTypes.ComponentCtaButton]: TypeComponentCtaButtonEntry
  [ComponentContentTypes.ComponentLink]: TypeComponentLinkEntry
  [ComponentContentTypes.ItemTestimonial]: TypeItemTestimonialEntry
  [ComponentContentTypes.ComponentCtaGroup]: TypeComponentCtaGroupEntry
  [ComponentContentTypes.SectionFaq]: TypeSectionFaqEntry
  [ComponentContentTypes.SectionHero]: TypeSectionHeroEntry
  [ComponentContentTypes.SectionMultiColumn]: TypeSectionMultiColumnEntry
  [ComponentContentTypes.SectionSingleColumn]: TypeSectionSingleColumnEntry
  [ComponentContentTypes.SectionTestimonials]: TypeSectionTestimonialsEntry
  [ComponentContentTypes.SectionTwoColumns]: TypeSectionTwoColumnEntry
  [ComponentContentTypes.SingleColumn]: TypeSingleColumnEntry
  [ComponentContentTypes.SectionGroup]: TypeSectionGroupEntry
  [ComponentContentTypes.VariationContainerAuthAndDeviceTypeCta]: TypeVariationContainerAuthAndDeviceTypeCtaEntry
  [ComponentContentTypes.VariationContainerDeviceType]: TypeVariationContainerDeviceTypeEntry
  [ComponentContentTypes.SectionTabs]: TypeSectionTabsEntry
}

export type FieldsOf<T> = T extends Entry<EntrySkeletonType> ? T['fields'] : never
export type EntryTypeByContentType<K extends ComponentContentTypes> = EntryTypeMap[K &
  keyof EntryTypeMap]

type AnyContentType = ComponentContentTypes

export const isOfType =
  <K extends AnyContentType>(id: K) =>
  (entry?: Entry<EntrySkeletonType>): entry is EntryTypeMap[K & keyof EntryTypeMap] =>
    !!entry && entry.sys?.contentType?.sys.id === id

export const isUnresolvedLink = (
  entry?: UnresolvedEntryLink | Entry<EntrySkeletonType>,
): entry is UnresolvedEntryLink => {
  return (
    !!entry &&
    entry.sys.type === 'Link' &&
    'linkType' in entry.sys &&
    entry.sys.linkType === 'Entry' &&
    !('fields' in entry)
  )
}
